import {IconButton} from '@dropbox/dig-components/buttons';
import {Tooltip} from '@dropbox/dig-components/tooltips';
import {Box, ThemeContainer, ThemeProvider} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {CloseLine, CommentLine} from '@dropbox/dig-icons/assets';
import {useLexicalComposerContext} from '@lexical/react/LexicalComposerContext';
import {useQueryClient} from '@tanstack/react-query';
import {employeeAtom} from 'atoms/employee';
import {derivedThemeAtom} from 'atoms/layout';
import {snackbarAtom} from 'atoms/snackbar';
import {useCollabId} from 'components/DSYS/CollabProvider';
import {$wrapSelectionInHighlightNode} from 'components/DSYS/editor/nodes/HighlightNode';
import styles from 'components/DSYS/editor/plugins/Comment.module.css';
import {
  clearLiveEditorSelectionForCommentMode,
  installEditorFocusGuard,
} from 'components/DSYS/editor/plugins/comments/commentFocus';
import {useThreads} from 'components/DSYS/editor/plugins/comments/useThreads';
import {MentionTypeaheadOption} from 'components/DSYS/editor/plugins/MentionPlugin';
import {pendingThreadIds} from 'components/DSYS/editor/plugins/TextSelectionPlugin';
import {ToolbarButton} from 'components/DSYS/editor/plugins/toolbar/ToolbarButton';
import {
  getPlanningCommentOriginIdFromElement,
  rememberPlanningCommentOrigin,
} from 'components/DSYS/editor/utils/planningCommentOrigin';
import {useAtomValue, useSetAtom} from 'jotai';
import {
  $createRangeSelection,
  $getRoot,
  $getSelection,
  $isElementNode,
  $isRangeSelection,
  $isTextNode,
  $setSelection,
} from 'lexical';
import {useEffect, useRef} from 'react';

import {CommentDisplay} from './CommentDisplay';

export interface CommentCreatedData {
  threadId: string;
  editorKey: string;
  commentText: string;
  planningCommentOriginId?: string | null;
}

interface CommentToolbarProps {
  commentActive: boolean;
  setCommentActive: (commentActive: boolean) => void;
  onClick?: () => void;
  onCommentClose?: () => void;
  onCommentCreated?: (data: CommentCreatedData) => void;
  onClearPendingHighlight?: () => void;
  maxHeight?: number;
  commentMentions?: MentionTypeaheadOption[];
}

const COMMENT_SELECTION_CONTEXT_CHARS = 24;
const MIN_CONTEXT_MATCH_CHARS = 8;

const getSelectionOriginElement = (fallback: HTMLElement | null): HTMLElement | null => {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) {
    return fallback;
  }

  const {commonAncestorContainer} = selection.getRangeAt(0);
  const selectionElement =
    commonAncestorContainer instanceof HTMLElement
      ? commonAncestorContainer
      : (commonAncestorContainer.parentElement ?? fallback);

  return getPlanningCommentOriginIdFromElement(selectionElement) ? selectionElement : fallback;
};

type SavedSelection = {
  text: string;
  anchorOffset: number;
  focusOffset: number;
  anchorKey: string;
  focusKey: string;
  globalStartOffset: number | null;
  globalEndOffset: number | null;
  contextBefore: string;
  contextAfter: string;
};

type TextNodeWithOffset = {
  node: any | null;
  startOffset: number;
  text: string;
};

type SelectionOffsets = {
  startOffset: number;
  endOffset: number;
};

const collectTextNodesWithOffsets = (node: any): TextNodeWithOffset[] => {
  const textNodes: TextNodeWithOffset[] = [];
  let runningOffset = 0;
  let prevWasElement = true;

  const appendSegment = (segmentNode: any | null, text: string): void => {
    if (!text) {
      return;
    }

    textNodes.push({node: segmentNode, startOffset: runningOffset, text});
    runningOffset += text.length;
  };

  const walk = (currentNode: any): void => {
    // Match RangeSelection#getTextContent so multi-block selections, like list
    // items inside one table cell, can be found after the live selection is cleared.
    if ($isElementNode(currentNode) && !currentNode.isInline()) {
      if (!prevWasElement) {
        appendSegment(null, '\n');
      }

      const children = currentNode.getChildren?.() ?? [];
      prevWasElement = children.length > 0;

      for (const child of children) {
        walk(child);
      }

      if (children.length === 0) {
        prevWasElement = false;
      }

      return;
    }

    if ($isTextNode(currentNode)) {
      const text = currentNode.getTextContent();
      appendSegment(currentNode, text);
      prevWasElement = false;
      return;
    }

    const children = currentNode.getChildren?.() ?? [];
    for (const child of children) {
      walk(child);
    }

    if (children.length === 0) {
      const text = currentNode.getTextContent?.() ?? '';
      appendSegment(null, text);
      if (text) {
        prevWasElement = false;
      }
    }
  };

  walk(node);
  return textNodes;
};

const getGlobalOffsetForNode = (
  textNodes: TextNodeWithOffset[],
  nodeKey: string,
  nodeOffset: number
): number | null => {
  const match = textNodes.find(({node}) => node?.getKey() === nodeKey);
  if (!match) {
    return null;
  }

  if (nodeOffset < 0 || nodeOffset > match.text.length) {
    return null;
  }

  return match.startOffset + nodeOffset;
};

const getNodePositionForGlobalOffset = (
  textNodes: TextNodeWithOffset[],
  globalOffset: number
): {key: string; offset: number} | null => {
  if (textNodes.length === 0) {
    return null;
  }

  if (globalOffset < 0) {
    return null;
  }

  const totalLength =
    textNodes[textNodes.length - 1].startOffset + textNodes[textNodes.length - 1].text.length;
  if (globalOffset > totalLength) {
    return null;
  }

  for (const {node, startOffset, text} of textNodes) {
    if (!node || text.length === 0) {
      continue;
    }

    const endOffset = startOffset + text.length;
    if (globalOffset >= startOffset && globalOffset <= endOffset) {
      return {key: node.getKey(), offset: globalOffset - startOffset};
    }
  }

  return null;
};

const getSharedSuffixLength = (a: string, b: string): number => {
  let shared = 0;
  const max = Math.min(a.length, b.length);
  while (shared < max && a[a.length - 1 - shared] === b[b.length - 1 - shared]) {
    shared += 1;
  }
  return shared;
};

const getSharedPrefixLength = (a: string, b: string): number => {
  let shared = 0;
  const max = Math.min(a.length, b.length);
  while (shared < max && a[shared] === b[shared]) {
    shared += 1;
  }
  return shared;
};

const resolveSelectionOffsetsFromSavedSelection = (
  savedSelection: SavedSelection,
  textNodes: TextNodeWithOffset[]
): SelectionOffsets | null => {
  if (!savedSelection.text) {
    return null;
  }

  const fullText = textNodes.map(({text}) => text).join('');
  if (fullText.length === 0) {
    return null;
  }

  const validateOffsets = (startOffset: number, endOffset: number): SelectionOffsets | null => {
    if (startOffset < 0 || endOffset <= startOffset || endOffset > fullText.length) {
      return null;
    }

    const selectedText = fullText.slice(startOffset, endOffset);
    if (selectedText !== savedSelection.text) {
      return null;
    }

    return {startOffset, endOffset};
  };

  const anchorGlobalOffset = getGlobalOffsetForNode(
    textNodes,
    savedSelection.anchorKey,
    savedSelection.anchorOffset
  );
  const focusGlobalOffset = getGlobalOffsetForNode(
    textNodes,
    savedSelection.focusKey,
    savedSelection.focusOffset
  );

  if (anchorGlobalOffset !== null && focusGlobalOffset !== null) {
    const fromKeys = validateOffsets(
      Math.min(anchorGlobalOffset, focusGlobalOffset),
      Math.max(anchorGlobalOffset, focusGlobalOffset)
    );
    if (fromKeys) {
      return fromKeys;
    }
  }

  if (savedSelection.globalStartOffset !== null && savedSelection.globalEndOffset !== null) {
    const fromGlobalOffsets = validateOffsets(
      savedSelection.globalStartOffset,
      savedSelection.globalEndOffset
    );
    if (fromGlobalOffsets) {
      return fromGlobalOffsets;
    }
  }

  const candidateOffsets: number[] = [];
  let searchFrom = 0;
  while (searchFrom <= fullText.length) {
    const nextIndex = fullText.indexOf(savedSelection.text, searchFrom);
    if (nextIndex === -1) {
      break;
    }
    candidateOffsets.push(nextIndex);
    searchFrom = nextIndex + 1;
  }

  if (candidateOffsets.length === 0) {
    return null;
  }

  if (candidateOffsets.length === 1) {
    return {
      startOffset: candidateOffsets[0],
      endOffset: candidateOffsets[0] + savedSelection.text.length,
    };
  }

  const scoredCandidates = candidateOffsets.map((startOffset) => {
    const endOffset = startOffset + savedSelection.text.length;
    const beforeContext = fullText.slice(
      Math.max(0, startOffset - savedSelection.contextBefore.length),
      startOffset
    );
    const afterContext = fullText.slice(endOffset, endOffset + savedSelection.contextAfter.length);
    const prefixMatch = getSharedSuffixLength(beforeContext, savedSelection.contextBefore);
    const suffixMatch = getSharedPrefixLength(afterContext, savedSelection.contextAfter);
    return {
      startOffset,
      endOffset,
      prefixMatch,
      suffixMatch,
      score: prefixMatch + suffixMatch,
      exactPrefix:
        savedSelection.contextBefore.length > 0 && beforeContext === savedSelection.contextBefore,
      exactSuffix:
        savedSelection.contextAfter.length > 0 && afterContext === savedSelection.contextAfter,
    };
  });

  scoredCandidates.sort((a, b) => b.score - a.score);

  const best = scoredCandidates[0];
  const runnerUp = scoredCandidates[1];
  const hasExactContextMatch = best.exactPrefix || best.exactSuffix;
  const hasStrongContextMatch = best.score >= MIN_CONTEXT_MATCH_CHARS;
  const unambiguous = !runnerUp || best.score > runnerUp.score;

  if (!unambiguous || (!hasExactContextMatch && !hasStrongContextMatch)) {
    return null;
  }

  return {startOffset: best.startOffset, endOffset: best.endOffset};
};

const applySelectionFromOffsets = (
  offsets: SelectionOffsets,
  textNodes: TextNodeWithOffset[]
): boolean => {
  const startPosition = getNodePositionForGlobalOffset(textNodes, offsets.startOffset);
  const endPosition = getNodePositionForGlobalOffset(textNodes, offsets.endOffset);

  if (!startPosition || !endPosition) {
    return false;
  }

  const restoredSelection = $createRangeSelection();
  restoredSelection.anchor.set(startPosition.key, startPosition.offset, 'text');
  restoredSelection.focus.set(endPosition.key, endPosition.offset, 'text');
  $setSelection(restoredSelection);
  return true;
};

export const CommentToolbar = ({
  commentActive,
  setCommentActive,
  onCommentClose,
  onCommentCreated,
  onClearPendingHighlight,
  maxHeight,
  commentMentions,
}: CommentToolbarProps) => {
  const collabId = useCollabId();
  const [editor] = useLexicalComposerContext();
  const isDarkMode = useAtomValue(derivedThemeAtom) === 'dark';
  const employee = useAtomValue(employeeAtom);
  const setSnackbarMessage = useSetAtom(snackbarAtom);
  const queryClient = useQueryClient();

  const {createThread} = useThreads();

  // Store selection info to survive DOM changes during comment creation
  const savedSelectionRef = useRef<SavedSelection | null>(null);

  // Capture and save the current selection when comment mode is activated
  useEffect(() => {
    if (commentActive && !savedSelectionRef.current) {
      let shouldClearEditorSelection = false;

      editor.getEditorState().read(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection) && !selection.isCollapsed()) {
          const anchor = selection.anchor;
          const focus = selection.focus;
          const root = $getRoot();
          const textNodes = collectTextNodesWithOffsets(root);
          const fullText = textNodes.map(({text}) => text).join('');
          const anchorGlobalOffset = getGlobalOffsetForNode(textNodes, anchor.key, anchor.offset);
          const focusGlobalOffset = getGlobalOffsetForNode(textNodes, focus.key, focus.offset);
          const globalStartOffset =
            anchorGlobalOffset !== null && focusGlobalOffset !== null
              ? Math.min(anchorGlobalOffset, focusGlobalOffset)
              : null;
          const globalEndOffset =
            anchorGlobalOffset !== null && focusGlobalOffset !== null
              ? Math.max(anchorGlobalOffset, focusGlobalOffset)
              : null;
          const contextBefore =
            globalStartOffset !== null
              ? fullText.slice(
                  Math.max(0, globalStartOffset - COMMENT_SELECTION_CONTEXT_CHARS),
                  globalStartOffset
                )
              : '';
          const contextAfter =
            globalEndOffset !== null
              ? fullText.slice(globalEndOffset, globalEndOffset + COMMENT_SELECTION_CONTEXT_CHARS)
              : '';

          savedSelectionRef.current = {
            text: selection.getTextContent(),
            anchorOffset: anchor.offset,
            focusOffset: focus.offset,
            anchorKey: anchor.key,
            focusKey: focus.key,
            globalStartOffset,
            globalEndOffset,
            contextBefore,
            contextAfter,
          };
          shouldClearEditorSelection = true;
        }
      });

      if (shouldClearEditorSelection) {
        // Keep the saved selection for submit, but clear Lexical's live selection so
        // later editor updates do not reconcile it back into DOM focus.
        clearLiveEditorSelectionForCommentMode(editor);
      }

      return installEditorFocusGuard(editor);
    } else if (!commentActive) {
      // Clear saved selection when comment mode is deactivated
      savedSelectionRef.current = null;
    }
  }, [commentActive, editor]);

  const handleCreateComment = async (commentText: string) => {
    if (!commentText.trim()) return;

    if (!createThread) {
      setSnackbarMessage({text: 'Comment creation is not available'});
      return;
    }

    if (!employee?.user_id) {
      return;
    }

    try {
      // Check if we have a valid selection OR a saved selection to restore
      let hasValidSelection = false;
      let hasRestorableSelection = false;
      let attachedText = savedSelectionRef.current?.text.trim() ?? '';
      editor.getEditorState().read(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection) && selection.getTextContent().trim()) {
          hasValidSelection = true;
          attachedText =
            savedSelectionRef.current?.text.trim() || selection.getTextContent().trim();
          return;
        }

        if (savedSelectionRef.current) {
          const textNodes = collectTextNodesWithOffsets($getRoot());
          hasRestorableSelection =
            resolveSelectionOffsetsFromSavedSelection(savedSelectionRef.current, textNodes) !==
            null;
          if (hasRestorableSelection) {
            attachedText = savedSelectionRef.current.text.trim();
          }
        }
      });

      // If no current selection, require an unambiguous saved selection to restore
      if (!hasValidSelection && !hasRestorableSelection) {
        setSnackbarMessage({
          text: 'The selected text has changed. Please reselect and try again.',
        });
        return;
      }

      const originElement = getSelectionOriginElement(editor.getRootElement());
      const planningCommentOriginId =
        getPlanningCommentOriginIdFromElement(originElement) ?? undefined;

      // Create thread using the provider's createThread function
      const threadId = await createThread({
        comment: commentText,
        employeeId: employee.user_id,
        attachedText,
      });
      rememberPlanningCommentOrigin(threadId, originElement);

      // Wrap the selected text with a highlight node containing the thread ID
      let selectionApplied = false;

      // Mark this thread as pending BEFORE the DOM update
      // This allows toggleHighlightedThreads to preserve styling synchronously
      pendingThreadIds.add(threadId);

      // Use discrete: true to force the update even on read-only editors
      editor.update(
        () => {
          let currentSelection = $getSelection();
          const currentText = $isRangeSelection(currentSelection)
            ? currentSelection.getTextContent()
            : '';

          // Check if selection needs restoration:
          // 1. If it's invalid (not a range, collapsed, or empty)
          // 2. If it exists but doesn't match our saved selection text (DOM may have changed)
          const needsRestore =
            !$isRangeSelection(currentSelection) ||
            currentSelection.isCollapsed() ||
            !currentSelection.getTextContent().trim() ||
            (savedSelectionRef.current && currentText !== savedSelectionRef.current.text);

          if (needsRestore && savedSelectionRef.current) {
            try {
              const textNodes = collectTextNodesWithOffsets($getRoot());
              const resolvedOffsets = resolveSelectionOffsetsFromSavedSelection(
                savedSelectionRef.current,
                textNodes
              );
              if (resolvedOffsets) {
                const restored = applySelectionFromOffsets(resolvedOffsets, textNodes);
                if (restored) {
                  currentSelection = $getSelection();
                }
              }
            } catch (error) {
              console.error('[wrapSelection] Failed to restore selection:', error);
            }
          }

          // Validate that we now have a valid range selection with text content
          if (
            $isRangeSelection(currentSelection) &&
            !currentSelection.isCollapsed() &&
            currentSelection.getTextContent().trim()
          ) {
            try {
              $wrapSelectionInHighlightNode(currentSelection, threadId, styles.comment);
              selectionApplied = true;
            } catch (error) {
              console.error('[wrapSelection] Failed to apply highlight:', error);
            }
          }
        },
        {discrete: true} // Force update even on read-only editors
      );

      // If selection couldn't be applied, warn the user
      if (!selectionApplied) {
        setSnackbarMessage({
          text: 'Comment created but the selected text has changed.',
        });
        pendingThreadIds.delete(threadId);
        onClearPendingHighlight?.();
        setCommentActive(false);
        return threadId;
      }

      // Clear pending state and show popup
      onClearPendingHighlight?.();
      setCommentActive(false);
      const editorKey = editor.getKey();
      onCommentCreated?.({
        threadId,
        editorKey,
        commentText,
        planningCommentOriginId,
      });

      queryClient.invalidateQueries({
        queryKey: ['document', 'threads', collabId],
      });

      return threadId;
    } catch (error) {
      setSnackbarMessage({text: 'Error creating comment: ' + (error as Error).message});
      onClearPendingHighlight?.();
    }
  };

  // If comment mode is active, render the comment input display
  if (commentActive) {
    return (
      <CommentDisplay
        onCreate={async (commentText: string) => {
          const threadId = await handleCreateComment(commentText);
          return threadId;
        }}
        onClose={() => {
          onCommentClose?.();
        }}
        maxHeightForInlineComment={maxHeight}
        commentMentions={commentMentions}
        // Note: onCommentCreated is called inside handleCreateComment for immediate feedback
        // Don't pass it here to avoid double-calling
      />
    );
  }

  // Render the comment button
  return (
    <ThemeProvider mode={!isDarkMode ? 'dark' : 'bright'}>
      <ThemeContainer>
        <Box
          boxShadow="Floating"
          backgroundColor="Background Base"
          borderRadius="Circular"
          borderStyle="Solid"
          borderColor="Border Subtle"
          style={{padding: 3}}
        >
          <Tooltip title={commentActive ? 'Close' : 'Add Comment'}>
            <IconButton variant="borderless" onClick={() => setCommentActive(!commentActive)}>
              <UIIcon src={commentActive ? CloseLine : CommentLine} />
            </IconButton>
          </Tooltip>
        </Box>
      </ThemeContainer>
    </ThemeProvider>
  );
};

export const CommentToolbarButton = ({
  commentActive,
  setCommentActive,
  onClick,
}: CommentToolbarProps) => {
  const handleClick = () => {
    (onClick || (() => setCommentActive(!commentActive)))();
  };

  return (
    <ToolbarButton
      title="Add comment"
      onClick={handleClick}
      icon={CommentLine}
      active={commentActive}
      // keycaps={['Command', 'Option', 'M']}
    />
  );
};
